/**
 * UTM parameter utility functions for tracking user acquisition sources
 */

// UTM parameter keys
export const UTM_KEYS = {
  SOURCE: 'utm_source',
  MEDIUM: 'utm_medium',
  CAMPAIGN: 'utm_campaign',
  TERM: 'utm_term',
  CONTENT: 'utm_content',
};

// LocalStorage key for UTM data
const UTM_STORAGE_KEY = 'utm_params';
const UTM_EXPIRY_KEY = 'utm_params_expiry';

// UTM data expiry time (30 days in milliseconds)
const UTM_EXPIRY_DURATION = 30 * 24 * 60 * 60 * 1000;

/**
 * Extract UTM parameters from URL search params
 * @param {URLSearchParams|string} searchParams - URL search parameters
 * @returns {Object} Object containing UTM parameters
 */
export const extractUtmParams = searchParams => {
  const params =
    typeof searchParams === 'string'
      ? new URLSearchParams(searchParams)
      : searchParams;

  const utmParams = {};

  Object.values(UTM_KEYS).forEach(key => {
    const value = params.get(key);
    if (value) {
      utmParams[key] = value;
    }
  });

  return utmParams;
};

/**
 * Store UTM parameters in localStorage with expiry
 * @param {Object} utmParams - UTM parameters object
 */
export const storeUtmParams = utmParams => {
  if (Object.keys(utmParams).length === 0) {
    console.log('No UTM parameters to store');
    return;
  }

  const expiryTime = Date.now() + UTM_EXPIRY_DURATION;

  try {
    localStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(utmParams));
    localStorage.setItem(UTM_EXPIRY_KEY, expiryTime.toString());
    console.log('💾 Stored UTM params:', utmParams);
    console.log('📅 Expiry time:', new Date(expiryTime).toISOString());
  } catch (error) {
    console.warn('Failed to store UTM parameters:', error);
  }
};

/**
 * Retrieve UTM parameters from localStorage (if not expired)
 * @returns {Object} UTM parameters object or empty object
 */
export const getStoredUtmParams = () => {
  try {
    const expiryTime = localStorage.getItem(UTM_EXPIRY_KEY);
    const currentTime = Date.now();

    // Check if expired
    if (!expiryTime || currentTime > parseInt(expiryTime)) {
      clearStoredUtmParams();
      return {};
    }

    const storedParams = localStorage.getItem(UTM_STORAGE_KEY);
    return storedParams ? JSON.parse(storedParams) : {};
  } catch (error) {
    console.warn('Failed to retrieve UTM parameters:', error);
    return {};
  }
};

/**
 * Clear stored UTM parameters
 */
export const clearStoredUtmParams = () => {
  try {
    localStorage.removeItem(UTM_STORAGE_KEY);
    localStorage.removeItem(UTM_EXPIRY_KEY);
  } catch (error) {
    console.warn('Failed to clear UTM parameters:', error);
  }
};

/**
 * Get UTM parameters from current URL and/or localStorage
 * Priority: URL params > stored params
 * @returns {Object} UTM parameters object
 */
export const getCurrentUtmParams = () => {
  // First try to get from current URL
  const urlParams = extractUtmParams(window.location.search);

  // If URL has UTM params, store them and return
  if (Object.keys(urlParams).length > 0) {
    storeUtmParams(urlParams);
    return urlParams;
  }

  // Otherwise, return stored params (if any)
  return getStoredUtmParams();
};

/**
 * Initialize UTM tracking on page load
 * Should be called when the app starts
 */
export const initUtmTracking = () => {
  try {
    console.log('🔍 Initializing UTM tracking...');
    console.log('Current URL:', window.location.href);

    const urlParams = extractUtmParams(window.location.search);
    console.log('Extracted UTM params:', urlParams);

    if (Object.keys(urlParams).length > 0) {
      storeUtmParams(urlParams);
      console.log('✅ UTM parameters stored in localStorage');
    } else {
      console.log('ℹ️ No UTM parameters found in URL');
    }
  } catch (error) {
    console.error('❌ Error initializing UTM tracking:', error);
  }
};

/**
 * Get UTM parameters formatted for API submission
 * @returns {Object} UTM parameters with snake_case keys for API
 */
export const getUtmParamsForApi = () => {
  const utmParams = getCurrentUtmParams();

  // Convert to the format expected by the API
  return {
    utm_source: utmParams[UTM_KEYS.SOURCE] || null,
    utm_medium: utmParams[UTM_KEYS.MEDIUM] || null,
    utm_campaign: utmParams[UTM_KEYS.CAMPAIGN] || null,
    utm_term: utmParams[UTM_KEYS.TERM] || null,
    utm_content: utmParams[UTM_KEYS.CONTENT] || null,
  };
};

/**
 * Debug function to manually test UTM tracking
 * Call this in browser console: window.testUTM()
 */
export const testUTMTracking = () => {
  console.log('🧪 Testing UTM Tracking...');
  console.log('Current URL:', window.location.href);
  console.log('Search params:', window.location.search);

  // Test extraction
  const extracted = extractUtmParams(window.location.search);
  console.log('Extracted UTM params:', extracted);

  // Test storage
  if (Object.keys(extracted).length > 0) {
    storeUtmParams(extracted);
  }

  // Test retrieval
  const stored = getStoredUtmParams();
  console.log('Stored UTM params:', stored);

  // Test API format
  const apiFormat = getUtmParamsForApi();
  console.log('API format:', apiFormat);

  // Check localStorage directly
  console.log(
    'Raw localStorage utm_params:',
    localStorage.getItem('utm_params'),
  );
  console.log(
    'Raw localStorage utm_params_expiry:',
    localStorage.getItem('utm_params_expiry'),
  );
};

// Make it available globally for testing
if (typeof window !== 'undefined') {
  window.testUTM = testUTMTracking;
}
